import { Ask, Back, Chart, Chevron, Eye, Mail, Plus } from "./Icons";
import { Line, ListSkeleton } from "./Skeleton";
import { LAST_RANGE, recall } from "../session";
import { DEFAULT_RANGE, RANGES, periodOf, type RangeKey } from "../stats/ranges";

/** The app, standing there before the app has arrived.
 *
 *  Opened from the home screen there is no browser chrome, no page left over
 *  from last time and nothing on the screen at all until React has been
 *  downloaded, parsed, run, and heard back from Firebase about who is holding
 *  the phone. Over cellular that is four round trips and a quarter of a
 *  megabyte, and what used to fill it was a spinner on an empty cream page —
 *  which says only "wait", and said it for a second and a half.
 *
 *  These say what is coming instead: the bar where the bar goes, the chips
 *  where the chips go, rows in their places. The same markup is written out by
 *  hand in index.html so that it paints the moment the HTML lands, long before
 *  any of this file exists on the phone; React then replaces it with something
 *  identical and nothing moves. The grey simply turns into leads.
 *
 *  So: anything changed here changes there too. index.html says the same. */

/** The bar, drawn flat. AppBar is the real one — it needs the router for its
 *  back button and a scroll listener for its hairline, and a shell has neither
 *  anywhere to go nor anything scrolled under it. */
function Bar({
  title,
  back,
  right,
  wide,
}: {
  title?: React.ReactNode;
  back?: boolean;
  right?: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <header className={`appbar${wide ? " appbar-wide" : ""}`}>
      <div className="appbar-inner">
        {back && (
          <span className="iconbtn" aria-hidden="true">
            <Back />
          </span>
        )}
        <div className="appbar-title grow">{title}</div>
        {right}
      </div>
    </header>
  );
}

/** The list. The chips are drawn selected on the filter the list will come
 *  back on, so the one that is lit does not change under the thumb. */
export function ListShell({ filter = "open" }: { filter?: string }) {
  return (
    <>
      <Bar
        wide
        title={
          <span className="row" style={{ gap: 8 }}>
            <img src="/logo.svg" alt="" width={20} height={20} />
            Studio
          </span>
        }
        right={
          <>
            <span className="iconbtn iconbtn-bar" aria-hidden="true">
              <Ask />
            </span>
            <span className="iconbtn iconbtn-bar" aria-hidden="true">
              <Chart />
            </span>
            <span className="iconbtn iconbtn-bar" aria-hidden="true">
              <Eye />
            </span>
            <span className="iconbtn iconbtn-bar" aria-hidden="true">
              <Mail />
            </span>
            <span className="btn btn-primary btn-sm" aria-hidden="true">
              <Plus size={17} />
              <span className="btn-label">New</span>
            </span>
          </>
        }
      />
      <div className="wrap wrap-wide">
        <div className="chiprow" aria-hidden="true">
          {[
            ["open", "To do"],
            ["sent", "Sent"],
            ["won", "Ordered"],
            ["all", "All"],
          ].map(([key, label]) => (
            <span className={`chip${key === filter ? " chip-on" : ""}`} key={key}>
              {label}
            </span>
          ))}
        </div>
        <div className="card">
          <ListSkeleton />
        </div>
      </div>
    </>
  );
}

/** One lead. The title is left empty on purpose: the name is the one thing
 *  here nobody can guess, and a placeholder word in its place would be read
 *  before it was replaced. */
export function LeadShell() {
  return (
    <>
      <Bar back />
      <div className="wrap">
        <div className="card stack" aria-hidden="true">
          <Line w="58%" h={22} />
          <Line w="40%" />
          <Line w="72%" />
        </div>
        <div className="card stack" aria-hidden="true">
          <Line w="34%" h={18} />
          <div className="skeleton" style={{ height: 180, borderRadius: 12 }} />
        </div>
      </div>
    </>
  );
}

/** A form has nothing to fetch, so this only ever covers the download of the
 *  screen itself — the title is the whole of the promise. */
export function FormShell() {
  return (
    <>
      <Bar back title="New lead" />
      <div className="wrap">
        <div className="card stack" aria-hidden="true">
          <Line w="30%" h={11} />
          <div className="skeleton" style={{ height: 46, borderRadius: 12 }} />
          <Line w="26%" h={11} />
          <div className="skeleton" style={{ height: 46, borderRadius: 12 }} />
          <Line w="38%" h={11} />
          <div className="skeleton" style={{ height: 46, borderRadius: 12 }} />
        </div>
      </div>
    </>
  );
}

/** The numbers, lit on the range the page will come back on, for the same
 *  reason the list's chips are. */
export function StatsShell() {
  const kept = recall(LAST_RANGE);
  const range = RANGES.some((r) => r.key === kept) ? (kept as RangeKey) : DEFAULT_RANGE;
  const period = periodOf(range, 0, Date.now());
  return (
    <>
      <Bar back wide title="Numbers" />
      <div className="wrap wrap-wide" aria-hidden="true">
        <div className="chiprow">
          {RANGES.map(({ key, label }) => (
            <span className={`chip${key === range ? " chip-on" : ""}`} key={key}>
              {label}
            </span>
          ))}
        </div>
        {range !== "all" && (
          <div className="stats-period">
            <span className="iconbtn">
              <Back size={20} />
            </span>
            <span className="iconbtn" style={{ opacity: 0.3 }}>
              <Chevron size={20} />
            </span>
            <span className="stats-period-label">
              <strong>{period.label}</strong>
              {period.dates && <span className="muted">{period.dates}</span>}
            </span>
          </div>
        )}
        <div className="stats-tiles">
          {[0, 1, 2, 3].map((i) => (
            <div className="stat stack" key={i}>
              <Line w="50%" h={11} />
              <Line w="40%" h={24} />
              <Line w="70%" h={11} />
            </div>
          ))}
        </div>
        <div className="card stack">
          <Line w="30%" h={18} />
          <div className="skeleton" style={{ height: 130, borderRadius: 12 }} />
        </div>
      </div>
    </>
  );
}

/** Ask. Nothing to fetch before the first question, so the bar and an empty
 *  page are the whole of what is coming. */
export function AskShell() {
  return (
    <>
      <Bar back wide title="Ask" />
      <div className="wrap wrap-wide" aria-hidden="true" />
    </>
  );
}

/** The newsletter list: the bar, the line of numbers and a few rows. */
export function NewsletterShell() {
  return (
    <>
      <Bar back wide title="Newsletter" />
      <div className="wrap wrap-wide" aria-hidden="true">
        <div className="card stack">
          <Line w="40%" h={18} />
          <Line w="60%" h={12} />
        </div>
        <div className="card">
          <ListSkeleton />
        </div>
      </div>
    </>
  );
}

/** What customers said once their piece arrived: the same bar and numbers as
 *  the newsletter, then a card or two where the reviews go. */
export function ReviewsShell() {
  return (
    <>
      <Bar back wide title="Reviews" />
      <div className="wrap wrap-wide" aria-hidden="true">
        <div className="card stack">
          <Line w="40%" h={18} />
          <Line w="60%" h={12} />
        </div>
        <div className="card stack">
          <Line w="50%" h={15} />
          <Line w="30%" h={12} />
          <div className="skeleton" style={{ height: 96, borderRadius: 12 }} />
        </div>
      </div>
    </>
  );
}

/** A customer page, before it has anything to show: the mark and one card.
 *  The same as #shell-preview in index.html, which every /p/ path paints. */
export function PreviewShell() {
  return (
    <div className="wrap wrap-top page selectable">
      <div className="preview-top">
        <img src="/logo.svg" alt="MiniME" height={30} />
      </div>
      <div className="card stack" aria-hidden="true">
        <div className="skeleton" style={{ height: 300, borderRadius: 12 }} />
        <div className="skeleton" style={{ height: 46, borderRadius: 999 }} />
      </div>
    </div>
  );
}
